import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import { useCurrency } from "../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Plus,
  CreditCard,
  Calendar,
  User,
  Banknote,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Printer,
  FileText,
  Trash2,
  Eye,
  DollarSign,
  Calculator,
  Receipt,
  MessageCircle,
} from "lucide-react";

const API = `${window.location.origin}/api`;

// دالة للمشاركة عبر واتساب
const shareInstallmentViaWhatsApp = (paymentData, shopInfo) => {
  let text = `*${shopInfo.name || "اسم المؤسسة"}*\n`;
  text += `${"═".repeat(20)}\n\n`;
  text += `📅 *وصل دفعة قسط*\n\n`;
  text += `🔢 رقم الوصل: ${paymentData.receiptNumber}\n`;
  text += `👤 العميل: ${paymentData.customerName}\n`;
  if (paymentData.customerPhone) text += `📞 الهاتف: ${paymentData.customerPhone}\n`;
  text += `\n🔢 رقم القسط: ${paymentData.installmentNumber} من ${paymentData.totalInstallments}\n`;
  text += `💰 المبلغ المدفوع: ${new Intl.NumberFormat("ar-IQ").format(paymentData.amount)} د.ع\n`;
  text += `📆 تاريخ الدفع: ${paymentData.date}\n`;
  if (paymentData.nextDueDate) text += `⏭️ القسط التالي: ${paymentData.nextDueDate}\n`;
  text += `\n💵 المتبقي: ${new Intl.NumberFormat("ar-IQ").format(paymentData.remainingAfter)} د.ع\n`;
  text += `\n${"═".repeat(20)}\n`;
  text += `_Powered by Nazario-Hesabaty_`;
  
  const encodedText = encodeURIComponent(text);
  const url = paymentData.customerPhone 
    ? `https://wa.me/${paymentData.customerPhone.replace(/\D/g, '')}?text=${encodedText}`
    : `https://wa.me/?text=${encodedText}`;
  window.open(url, "_blank");
};

export default function InstallmentSalesPage() {
  const { user } = useAuth();
  const [installments, setInstallments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [counter, setCounter] = useState(0);

  // نموذج إنشاء تقسيط جديد
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    total_amount: "",
    down_payment: "0",
    num_installments: "3",
    installment_interval: "30", // أيام بين كل قسط
    notes: "",
    items: [],
  });

  // نموذج دفعة
  const [paymentData, setPaymentData] = useState({
    amount: "",
    notes: "",
    payer_name: "",
  });

  // إحصائيات
  const [stats, setStats] = useState({
    total_active: 0,
    total_remaining: 0,
    overdue_count: 0,
    paid_this_month: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [installmentsRes, customersRes, productsRes, counterRes] = await Promise.all([
        axios.get(`${API}/installments`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/installments/counter`).catch(() => ({ data: { count: 0 } })),
      ]);
      
      setInstallments(installmentsRes.data || []);
      setCustomers(customersRes.data || []);
      setProducts(productsRes.data || []);
      setCounter(counterRes.data.count || 0);
      
      // حساب الإحصائيات
      const activeInstallments = (installmentsRes.data || []).filter(i => i.status !== 'completed');
      const totalRemaining = activeInstallments.reduce((sum, i) => sum + (i.remaining_amount || 0), 0);
      const overdueCount = activeInstallments.filter(i => i.next_due_date && new Date(i.next_due_date) < new Date()).length;
      
      setStats({
        total_active: activeInstallments.length,
        total_remaining: totalRemaining,
        overdue_count: overdueCount,
        paid_this_month: 0,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-IQ").format(amount || 0) + " د.ع";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateInstallmentAmount = () => {
    const total = parseFloat(formData.total_amount) || 0;
    const downPayment = parseFloat(formData.down_payment) || 0;
    const numInstallments = parseInt(formData.num_installments) || 1;
    const remaining = total - downPayment;
    return Math.ceil(remaining / numInstallments);
  };

  const handleCreateInstallment = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.total_amount) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/installments`, {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        down_payment: parseFloat(formData.down_payment) || 0,
        num_installments: parseInt(formData.num_installments),
        installment_interval: parseInt(formData.installment_interval),
      });
      
      toast.success("تم إنشاء عملية التقسيط بنجاح");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إنشاء التقسيط");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedInstallment || !paymentData.amount) {
      toast.error("يرجى إدخال المبلغ");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/installments/${selectedInstallment.id}/payment`, {
        amount: parseFloat(paymentData.amount),
        notes: paymentData.notes,
        payer_name: paymentData.payer_name || user?.name || "المدير",
      });
      
      // حساب تاريخ القسط التالي
      const intervalDays = selectedInstallment.installment_interval || 30;
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + intervalDays);
      const nextDueDateFormatted = nextDate.toLocaleDateString('ar-IQ', { 
        year: 'numeric', month: 'long', day: 'numeric'
      });
      
      // حساب رقم القسط
      const paidInstallments = selectedInstallment.payments?.length || 0;
      const installmentNumber = paidInstallments + 1;
      
      const newCounter = counter + 1;
      setLastPayment({
        receiptNumber: `Q${String(newCounter).padStart(7, '0')}`,
        customerName: selectedInstallment.customer_name,
        customerPhone: selectedInstallment.customer_phone,
        amount: parseFloat(paymentData.amount),
        remainingAfter: selectedInstallment.remaining_amount - parseFloat(paymentData.amount),
        payerName: paymentData.payer_name || user?.name || "المدير",
        installmentNumber: installmentNumber,
        totalInstallments: selectedInstallment.num_installments,
        nextDueDate: (selectedInstallment.remaining_amount - parseFloat(paymentData.amount)) > 0 ? nextDueDateFormatted : null,
        date: new Date().toLocaleDateString('ar-IQ', { 
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }),
      });
      setCounter(newCounter);
      
      toast.success("تم تسجيل الدفعة بنجاح");
      setIsPaymentDialogOpen(false);
      setPaymentData({ amount: "", notes: "", payer_name: "" });
      setShowReceipt(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تسجيل الدفعة");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: "",
      customer_phone: "",
      total_amount: "",
      down_payment: "0",
      num_installments: "3",
      installment_interval: "30",
      notes: "",
      items: [],
    });
  };

  const openPaymentDialog = (installment) => {
    setSelectedInstallment(installment);
    // اقتراح قيمة القسط التالي
    const suggestedAmount = installment.installment_amount || 
      Math.ceil(installment.remaining_amount / (installment.remaining_installments || 1));
    setPaymentData({ 
      amount: suggestedAmount.toString(), 
      notes: "", 
      payer_name: user?.name || "" 
    });
    setIsPaymentDialogOpen(true);
  };

  const openDetailDialog = (installment) => {
    setSelectedInstallment(installment);
    setIsDetailDialogOpen(true);
  };

  const printReceipt = () => {
    if (!lastPayment) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>وصل دفعة قسط - ${lastPayment.receiptNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
          .receipt { border: 2px solid #333; padding: 20px; border-radius: 10px; }
          .header { text-align: center; border-bottom: 2px dashed #333; padding-bottom: 15px; margin-bottom: 15px; }
          .header h1 { font-size: 20px; margin-bottom: 5px; }
          .receipt-num { font-size: 24px; font-weight: bold; color: #7c3aed; }
          .section { margin: 10px 0; padding: 10px; background: #f5f5f5; border-radius: 5px; }
          .amount-section { text-align: center; background: #e8f5e9; padding: 15px; border-radius: 10px; margin: 15px 0; }
          .amount { font-size: 28px; font-weight: bold; color: #2e7d32; }
          .signature { margin-top: 30px; padding-top: 20px; border-top: 1px dashed #333; text-align: center; }
          .date { text-align: center; color: #666; font-size: 12px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1 style="font-size: 24px; color: #7c3aed; margin-bottom: 8px;">${user?.shop_name || "اسم المؤسسة"}</h1>
            ${user?.phone ? `<div style="font-size: 12px; color: #666; margin-bottom: 8px;">📞 ${user.phone}</div>` : ''}
            <div style="border-top: 2px solid #7c3aed; margin: 8px 0;"></div>
            <h2 style="font-size: 18px; margin-bottom: 5px;">وصل دفعة قسط</h2>
            <div class="receipt-num">${lastPayment.receiptNumber}</div>
          </div>
          <div class="section"><strong>الزبون:</strong> ${lastPayment.customerName}</div>
          <div class="section"><strong>رقم القسط:</strong> ${lastPayment.installmentNumber || "-"} من ${lastPayment.totalInstallments || "-"}</div>
          <div class="amount-section">
            <p>المبلغ المدفوع</p>
            <div class="amount">${formatCurrency(lastPayment.amount)}</div>
          </div>
          <div class="section"><strong>المتبقي:</strong> ${formatCurrency(lastPayment.remainingAfter)}</div>
          ${lastPayment.nextDueDate ? `<div class="section" style="background: #fff3cd;"><strong>📅 القسط التالي:</strong> ${lastPayment.nextDueDate}</div>` : ''}
          <div class="section"><strong>المستلم:</strong> ${lastPayment.payerName}</div>
          <div class="signature">التوقيع: _____________________</div>
          <div class="date">${lastPayment.date}</div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (status, nextDueDate) => {
    const isOverdue = nextDueDate && new Date(nextDueDate) < new Date();
    
    if (status === 'completed') {
      return <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400">مكتمل</span>;
    } else if (isOverdue) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">متأخر</span>;
    } else {
      return <span className="px-2 py-1 text-xs rounded-full bg-amber-500/20 text-amber-400">قيد السداد</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="installment-sales-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30">
              <CreditCard className="w-6 h-6 text-purple-400" />
            </div>
            البيع بالتقسيط
          </h1>
          <p className="text-slate-400 mt-1">
            إدارة المبيعات بالتقسيط ومتابعة الدفعات
          </p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-600/25"
          data-testid="new-installment-btn"
        >
          <Plus className="w-5 h-5 me-2" />
          تقسيط جديد
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-purple-500/20">
              <CreditCard className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">التقسيطات النشطة</p>
              <p className="text-2xl font-bold text-white">{stats.total_active}</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-500/20">
              <DollarSign className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">إجمالي المستحق</p>
              <p className="text-xl font-bold text-white">{formatCurrency(stats.total_remaining)}</p>
              <USDHint amount={stats.total_remaining} />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-rose-500/10 border border-red-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">أقساط متأخرة</p>
              <p className="text-2xl font-bold text-white">{stats.overdue_count}</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">مكتملة</p>
              <p className="text-2xl font-bold text-white">
                {installments.filter(i => i.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Installments List */}
      {installments.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 rounded-xl border border-slate-700">
          <CreditCard className="w-20 h-20 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 text-lg mb-4">
            لا توجد عمليات تقسيط بعد
          </p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 me-2" />
            إنشاء أول تقسيط
          </Button>
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">الزبون</TableHead>
                    <TableHead className="text-slate-300">المبلغ الكلي</TableHead>
                    <TableHead className="text-slate-300">المتبقي</TableHead>
                    <TableHead className="text-slate-300">القسط التالي</TableHead>
                    <TableHead className="text-slate-300">الحالة</TableHead>
                    <TableHead className="text-slate-300 text-center">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {installments.map((installment) => (
                    <TableRow key={installment.id} className="border-slate-700 hover:bg-slate-700/50">
                      <TableCell className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-purple-400" />
                          </div>
                          <div>
                            <p className="font-medium">{installment.customer_name}</p>
                            {installment.customer_phone && (
                              <p className="text-xs text-slate-400">{installment.customer_phone}</p>
                            )}
                            {installment.linked_sale_invoice && (
                              <p className="text-[10px] text-emerald-400 mt-0.5 flex items-center gap-1" title="مرتبطة بفاتورة POS">
                                🔗 {installment.linked_sale_invoice}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {formatCurrency(installment.total_amount)}
                      </TableCell>
                      <TableCell className="text-amber-400 font-bold">
                        {formatCurrency(installment.remaining_amount)}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          {formatDate(installment.next_due_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(installment.status, installment.next_due_date)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailDialog(installment)}
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {installment.status !== 'completed' && (
                            <Button
                              size="sm"
                              onClick={() => openPaymentDialog(installment)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Banknote className="w-4 h-4 me-1" />
                              دفعة
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Installment Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-400" />
              إنشاء تقسيط جديد
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInstallment} className="space-y-5 mt-4">
            {/* معلومات الزبون */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-200">اسم الزبون *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="اسم الزبون"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">رقم الهاتف</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="07XXXXXXXXX"
                  className="bg-slate-800 border-slate-700 text-white"
                  dir="ltr"
                />
              </div>
            </div>

            {/* المبلغ الكلي */}
            <div className="space-y-2">
              <Label className="text-slate-200">المبلغ الكلي *</Label>
              <PriceInput
                value={parseFloat(formData.total_amount) || 0}
                onChange={(v) => setFormData({ ...formData, total_amount: String(v) })}
                placeholder="0"
                testId="total-amount-input"
                className="bg-slate-800 border-slate-700 text-white text-2xl font-bold text-center py-6"
              />
            </div>

            {/* الدفعة الأولى */}
            <div className="space-y-2">
              <Label className="text-slate-200">الدفعة المقدمة</Label>
              <PriceInput
                value={parseFloat(formData.down_payment) || 0}
                onChange={(v) => setFormData({ ...formData, down_payment: String(v) })}
                placeholder="0"
                testId="down-payment-input"
                className="bg-slate-800 border-slate-700 text-white text-lg text-center"
              />
            </div>

            {/* عدد الأقساط */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-200">عدد الأقساط</Label>
                <Select
                  value={formData.num_installments}
                  onValueChange={(v) => setFormData({ ...formData, num_installments: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                      <SelectItem key={n} value={n.toString()} className="text-white hover:bg-slate-700">
                        {n} أقساط
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">الفترة بين الأقساط</Label>
                <Select
                  value={formData.installment_interval}
                  onValueChange={(v) => setFormData({ ...formData, installment_interval: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="7" className="text-white hover:bg-slate-700">أسبوع</SelectItem>
                    <SelectItem value="14" className="text-white hover:bg-slate-700">أسبوعين</SelectItem>
                    <SelectItem value="30" className="text-white hover:bg-slate-700">شهر</SelectItem>
                    <SelectItem value="60" className="text-white hover:bg-slate-700">شهرين</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ملخص الأقساط */}
            {formData.total_amount && (
              <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-slate-400 text-sm">قيمة كل قسط</p>
                    <p className="text-2xl font-bold text-purple-400">
                      {formatCurrency(calculateInstallmentAmount())}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">المتبقي للتقسيط</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {formatCurrency((parseFloat(formData.total_amount) || 0) - (parseFloat(formData.down_payment) || 0))}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ملاحظات */}
            <div className="space-y-2">
              <Label className="text-slate-200">ملاحظات (اختياري)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="أي ملاحظات إضافية..."
                className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
              />
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setIsDialogOpen(false); resetForm(); }}
                className="border-slate-600 text-slate-300 px-6"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "إنشاء التقسيط"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-400" />
              تسجيل دفعة
            </DialogTitle>
          </DialogHeader>
          
          {selectedInstallment && (
            <div className="p-4 rounded-xl bg-slate-800 border border-slate-700 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{selectedInstallment.customer_name}</p>
                  <p className="text-slate-400 text-sm">المتبقي: {formatCurrency(selectedInstallment.remaining_amount)}</p>
                </div>
                <div className="text-left">
                  <p className="text-purple-400 font-bold">
                    {selectedInstallment.paid_installments || 0}/{selectedInstallment.num_installments}
                  </p>
                  <p className="text-slate-500 text-xs">قسط مدفوع</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handlePayment} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-200">مبلغ الدفعة *</Label>
              <PriceInput
                value={parseFloat(paymentData.amount) || 0}
                onChange={(v) => setPaymentData({ ...paymentData, amount: String(v) })}
                placeholder="0"
                testId="installment-payment-input"
                className="bg-slate-800 border-slate-700 text-white text-2xl font-bold text-center py-6"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">اسم المستلم</Label>
              <Input
                value={paymentData.payer_name}
                onChange={(e) => setPaymentData({ ...paymentData, payer_name: e.target.value })}
                placeholder="اسم الشخص الذي استلم الدفعة"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">ملاحظات (اختياري)</Label>
              <Input
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="أي ملاحظات..."
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPaymentDialogOpen(false)}
                className="border-slate-600 text-slate-300 px-6"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "تسجيل الدفعة"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              تفاصيل التقسيط
            </DialogTitle>
          </DialogHeader>
          
          {selectedInstallment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">الزبون</p>
                  <p className="text-white font-medium">{selectedInstallment.customer_name}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">الهاتف</p>
                  <p className="text-white font-medium">{selectedInstallment.customer_phone || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-800 rounded-lg text-center">
                  <p className="text-slate-400 text-xs">المبلغ الكلي</p>
                  <p className="text-white font-bold">{formatCurrency(selectedInstallment.total_amount)}</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center border border-emerald-500/30">
                  <p className="text-slate-400 text-xs">المدفوع</p>
                  <p className="text-emerald-400 font-bold">{formatCurrency(selectedInstallment.paid_amount)}</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg text-center border border-amber-500/30">
                  <p className="text-slate-400 text-xs">المتبقي</p>
                  <p className="text-amber-400 font-bold">{formatCurrency(selectedInstallment.remaining_amount)}</p>
                </div>
              </div>

              <div className="p-4 bg-slate-800 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400">التقدم</span>
                  <span className="text-purple-400 font-bold">
                    {selectedInstallment.paid_installments || 0}/{selectedInstallment.num_installments} قسط
                  </span>
                </div>
                <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                    style={{ 
                      width: `${((selectedInstallment.paid_installments || 0) / selectedInstallment.num_installments) * 100}%` 
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">قيمة كل قسط</p>
                  <p className="text-white font-medium">{formatCurrency(selectedInstallment.installment_amount)}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">القسط التالي</p>
                  <p className="text-white font-medium">{formatDate(selectedInstallment.next_due_date)}</p>
                </div>
              </div>

              {selectedInstallment.notes && (
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">ملاحظات</p>
                  <p className="text-white">{selectedInstallment.notes}</p>
                </div>
              )}

              {/* سجل الدفعات */}
              {selectedInstallment.payments && selectedInstallment.payments.length > 0 && (
                <div className="p-4 bg-slate-800 rounded-xl">
                  <h4 className="text-white font-medium mb-3">سجل الدفعات</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedInstallment.payments.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-900 rounded-lg">
                        <div>
                          <p className="text-emerald-400 font-medium">{formatCurrency(payment.amount)}</p>
                          <p className="text-slate-500 text-xs">{formatDate(payment.date)}</p>
                        </div>
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDetailDialogOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              وصل الدفعة
            </DialogTitle>
          </DialogHeader>
          
          {lastPayment && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-slate-800 rounded-xl border border-slate-700">
                <p className="text-slate-400 text-sm">رقم الوصل</p>
                <p className="text-2xl font-bold text-purple-400">{lastPayment.receiptNumber}</p>
              </div>
              
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-slate-400 text-xs">الزبون</p>
                <p className="text-white font-medium">{lastPayment.customerName}</p>
              </div>
              
              <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                <p className="text-slate-400 text-sm">المبلغ المدفوع</p>
                <p className="text-3xl font-bold text-emerald-400">{formatCurrency(lastPayment.amount)}</p>
              </div>

              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-slate-400 text-xs">المتبقي بعد الدفعة</p>
                <p className="text-amber-400 font-bold">{formatCurrency(lastPayment.remainingAfter)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">المستلم</p>
                  <p className="text-white font-medium">{lastPayment.payerName}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">التاريخ</p>
                  <p className="text-white font-medium text-sm">{lastPayment.date}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="border-slate-600 text-slate-300"
            >
              إغلاق
            </Button>
            <Button
              onClick={() => shareInstallmentViaWhatsApp(lastPayment, { name: user?.shop_name, phone: user?.phone })}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 me-2" />
              واتساب
            </Button>
            <Button
              onClick={printReceipt}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Printer className="w-4 h-4 me-2" />
              طباعة الوصل
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
